import { SessionUser } from 'src/models/user/_types';
import { isAdmin, isSupportRole } from 'src/models/user/utils';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { timestampToDate } from 'src/utils/dateFormatter';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import { removeReadOnlyFields } from 'src/utils/fieldNormalizers';

import { InviteLink, Platform, PlatformSettings } from './_types';

const readOnlyFields = [
  'stripe_account_id',
  'platform_roles',
  'trainer_id',
  'use_stripe_express',
  'stripe_location_id',
  'brand',
  'subdomain_url',
  'current_ios_version',
  'current_android_version',
  'managed_apps_number',
  'apple_api_key',
  'id',
  'created_at',
  'updated_at',
  'connected_to',
  'trainer_name',
  'trainer_email',
];

// Fields that should be removed when serializing if user is not admin or support
const restrictedFields = [
  'ios_download_link',
  'android_download_link',
  'feature_flags',
  'enable_hq',
  'logo_url',
  'max_weeks',
  'transaction_fee_per',
  'transaction_fee_amount',
  'email_logo_url',
  'fbm_transaction_fee_per',
  'invoice_fee_amount',
  'invoice_fee_per',
  'fbm_transaction_fee_amount',
  'ach_transaction_fee_per',
  'ach_transaction_fee_amount',
  'ach_transaction_cap',
  'custom_app_menu',
  'app_logger_nav',
  'app_logger_workout_nav',
  'profile_menu',
  'custom_app_tabs',
  'default_currency',
  'au_becs_debit_fee_amount',
  'au_becs_debit_fee_per',
  'au_becs_debit_cap',
  'bacs_debit_fee_amount',
  'bacs_debit_fee_per',
  'bacs_debit_cap',
  'sepa_debit_fee_amount',
  'sepa_debit_fee_per',
  'sepa_debit_cap',
  'acss_debit_fee_amount',
  'acss_debit_fee_per',
  'acss_debit_cap',
  'klarna_ecommerce_fee_amount',
  'klarna_ecommerce_fee_per',
  'klarna_fbm_fee_amount',
  'klarna_fbm_fee_per',
  'klarna_ecommerce_cap',
  'klarna_fbm_cap',
  'afterpay_clearpay_fee_amount',
  'afterpay_clearpay_fee_per',
  'afterpay_clearpay_cap',
  'stripe_sepa_debit_fee_cap',
  'stripe_sepa_debit_fee_per',
  'stripe_sepa_debit_fee_amount',
  'stripe_ach_fee_cap',
  'stripe_ach_fee_per',
  'stripe_ach_fee_amount',
  'stripe_invoice_fee_per',
  'stripe_invoice_fee_amount',
  'stripe_card_fee_per',
  'stripe_card_fee_amount',
  'stripe_bacs_debit_fee_cap',
  'stripe_bacs_debit_fee_per',
  'stripe_bacs_debit_fee_amount',
  'stripe_au_becs_debit_fee_cap',
  'stripe_au_becs_debit_fee_per',
  'stripe_au_becs_debit_fee_amount',
  'stripe_acss_debit_fee_cap',
  'stripe_acss_debit_fee_per',
  'stripe_acss_debit_fee_amount',
  'stripe_klarna_fee_per',
  'stripe_klarna_fee_amount',
  'stripe_afterpay_clearpay_per',
  'stripe_afterpay_clearpay_amount',
  'iap',
  'app_bundle_id',
  'app_android_bundle_id',
  'app_apple_id',
  'app_apple_team_id',
  'app_itc_team_id',
  'app_firebase_id',
  'app_itc_team_name',
  'app_itc_team_display_name',
  'app_itc_app_password',
  'app_product_app_id',
  'app_short_name',
  'app_long_name',
  'platform_type',
  'location_stripe_plan',
  'parent_id',
  'supported_entry_types',
  'default_workout_type',
  'use_our_content',
  'use_exercises',
  'host',
  'subdomain',
  'app_dashboard',
  'make_all_accounts_clients',
  'sms_price_per',
  'sms_flat_price',
  'sms_free_count',
  'do_not_truncate_sms',
  'client_count',
  'price_per_client',
  'max_locations',
  'stripe_tax_calculation_per',
  'stripe_tax_transaction_per',
  'stripe_tax_subscription_per',
  'stripe_tax_fixed_invoice_fee',
  'darkmode_logo_url',
];

export function normalizePlatform(platform): Platform {
  const normalized = camelcaseKeys(platform, { deep: true });

  return {
    ...normalized,
    createdAt: timestampToDate(normalized.createdAt),
    updatedAt: timestampToDate(normalized.updatedAt),
  };
}

export function denormalizePlatform(
  platform,
  user?: SessionUser | null
): Platform {
  const decamelized = decamelizeKeys(platform, { deep: true });
  removeReadOnlyFields(decamelized, readOnlyFields);

  // Remove restricted fields if user is not admin or support
  if (user && !isAdmin(user) && !isSupportRole(user)) {
    restrictedFields.forEach((field) => {
      delete decamelized[field];
    });
  }

  // Remove the auto-decamelized version
  delete decamelized.twilio10_dlc_excluded;

  // Handle platform_roles separately since it's in readOnlyFields
  // but we need to denormalize the nested hqPermissions structure
  const denormalizedPlatformRoles = platform.platformRoles?.map((role) => {
    if (role.permissions?.hqPermissions) {
      return {
        ...role,
        permissions: {
          ...role.permissions,
          hqPermissions: decamelizeKeys(role.permissions.hqPermissions, {
            deep: true,
          }),
        },
      };
    }
    return role;
  });

  return {
    ...decamelized,
    // Handle special case for twilio10dlcExcluded
    ...(platform.twilio10dlcExcluded !== undefined && {
      twilio_10dlc_excluded: platform.twilio10dlcExcluded,
    }),
    // Include platform_roles with denormalized hqPermissions
    ...(denormalizedPlatformRoles && {
      platform_roles: denormalizedPlatformRoles,
    }),
  };
}

export function normalizePlatformSettings(platformSettings): PlatformSettings {
  return camelcaseKeys(platformSettings, { deep: true });
}

export function denormalizePlatformSettings(
  platformSettings,
  user?: SessionUser | null
): PlatformSettings {
  // Extract defaultChildPlatformRoles to prevent deep decamelization of role permissions
  const { defaultChildPlatformRoles, ...rest } = platformSettings;

  const decamelized = decamelizeKeys(rest, { deep: true });
  removeReadOnlyFields(decamelized, readOnlyFields);

  // Remove restricted fields if user is not admin or support
  if (user && !isAdmin(user) && !isSupportRole(user)) {
    restrictedFields.forEach((field) => {
      delete decamelized[field];
    });
  }

  // Add back defaultChildPlatformRoles with camelCase permissions preserved
  // Only convert the top-level key to snake_case
  if (defaultChildPlatformRoles !== undefined) {
    decamelized.default_child_platform_roles = defaultChildPlatformRoles;
  }

  return decamelized;
}

export function normalizeInviteLink(inviteLink): InviteLink {
  return camelcaseKeys(inviteLink, { deep: true });
}
